import { useEffect, useRef, useState } from "react";
import { FileUp, CheckCircle2, XCircle, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Field, inputCls } from "@/components/AuthShell";
import { api, errMsg, guessTitle, LEVELS, SUBJECTS } from "@/lib/api";

const MAX = 50 * 1024 * 1024;

export const BulkUploadDialog = ({ open, onOpenChange, onDone, isSample = false }) => {
  const [subject, setSubject] = useState("arabic");
  const [level, setLevel] = useState(1);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setItems([]); setRunning(false); }
  }, [open]);

  const add = (files) => {
    const list = Array.from(files || []).map((f, i) => {
      const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
      const error = !isPdf ? "ليس ملف PDF" : f.size > MAX ? "أكبر من 50 ميغابايت" : "";
      return { key: `${f.name}-${f.size}-${Date.now()}-${i}`, file: f, title: guessTitle(f.name), status: error ? "error" : "ready", error, pct: 0 };
    });
    setItems((s) => [...s, ...list]);
  };
  const patch = (key, p) => setItems((s) => s.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const start = async () => {
    setRunning(true);
    let ok = 0;
    for (const it of items) {
      if (it.status !== "ready") continue;
      patch(it.key, { status: "uploading", pct: 0 });
      const fd = new FormData();
      fd.append("file", it.file);
      fd.append("title", it.title.trim());
      fd.append("subject", subject);
      fd.append("level", level);
      fd.append("is_sample", isSample);
      try {
        await api.post("/admin/books", fd, { onUploadProgress: (e) => patch(it.key, { pct: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }) });
        patch(it.key, { status: "done", pct: 100 });
        ok += 1;
      } catch (e) {
        patch(it.key, { status: "error", error: errMsg(e) });
      }
    }
    setRunning(false);
    onDone(ok);
  };

  const ready = items.filter((x) => x.status === "ready").length;
  const done = items.filter((x) => x.status === "done").length;

  return (
    <Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
      <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="admin-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-right">{isSample ? "رفع عدة مصادر مجانية دفعة واحدة" : "رفع عدة كتب دفعة واحدة"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="المادة">
              <select disabled={running} className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="admin-bulk-subject">
                {SUBJECTS.map((s) => <option key={s.id} value={s.id}>{s.ar}</option>)}
              </select>
            </Field>
            <Field label="المستوى">
              <select disabled={running} className={inputCls} value={level} onChange={(e) => setLevel(Number(e.target.value))} data-testid="admin-bulk-level">
                {LEVELS.map((l) => <option key={l.n} value={l.n}>{l.n} · {l.name}</option>)}
              </select>
            </Field>
          </div>

          <button type="button" disabled={running} onClick={() => inputRef.current?.click()} data-testid="admin-bulk-pick-btn"
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (!running) add(e.dataTransfer.files); }}
            className="w-full border-2 border-dashed border-[#E5DCCB] hover:border-[#8B3A2B] bg-white p-6 text-center transition-colors disabled:opacity-50">
            <span className="flex flex-col items-center gap-2 text-sm text-stone-600"><FileUp className="w-7 h-7 text-[#8B3A2B]" /> اسحب عدة ملفات PDF هنا أو اضغط لاختيارها (حتى 50 ميغابايت لكل ملف)</span>
          </button>
          <input ref={inputRef} type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="admin-bulk-file-input" />

          {items.length > 0 && (
            <div className="bg-white border border-[#E5DCCB] divide-y divide-[#E5DCCB] max-h-72 overflow-y-auto" data-lenis-prevent data-testid="admin-bulk-list">
              {items.map((it, i) => (
                <div key={it.key} className="p-3 space-y-2" data-testid={`admin-bulk-item-${i}`}>
                  <div className="flex items-center gap-2">
                    {it.status === "done" && <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />}
                    {it.status === "error" && <XCircle className="w-5 h-5 text-red-700 shrink-0" />}
                    {it.status === "uploading" && <Loader2 className="w-5 h-5 text-[#B45309] animate-spin shrink-0" />}
                    {it.status === "ready" && <span className="w-5 text-center text-xs text-stone-500 shrink-0">{i + 1}</span>}
                    <input disabled={running || it.status !== "ready"} value={it.title} placeholder="العنوان (اختياري)" onChange={(e) => patch(it.key, { title: e.target.value })}
                      className="flex-1 min-w-0 h-9 px-3 bg-[#FAF7F2] border border-[#E5DCCB] rounded-sm text-sm focus:outline-none focus:border-[#8B3A2B]" dir="auto" data-testid={`admin-bulk-title-${i}`} />
                    <span className="text-xs text-stone-600 shrink-0" dir="ltr">{(it.file.size / 1048576).toFixed(1)} MB</span>
                    {!running && it.status !== "done" && (
                      <button onClick={() => setItems((s) => s.filter((x) => x.key !== it.key))} className="p-1.5 rounded-full text-red-800 hover:bg-red-50" aria-label="إزالة" data-testid={`admin-bulk-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                  {it.status === "uploading" && <Progress value={it.pct} className="h-1.5" />}
                  {it.error && <p className="text-xs text-red-800">{it.error}</p>}
                </div>
              ))}
            </div>
          )}

          {items.length > 0 && <p className="text-sm text-stone-700" data-testid="admin-bulk-summary">{items.length} ملف · جاهز للرفع {ready} · تم رفع {done}</p>}

          <button onClick={start} disabled={running || !ready} className="w-full h-12 rounded-full bg-[#1C1917] text-[#FAF7F2] font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-50" data-testid="admin-bulk-submit-btn">
            {running ? `جارٍ الرفع... (${done} من ${done + items.filter((x) => x.status === "ready" || x.status === "uploading").length})` : `رفع ${ready} كتاب`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
